import * as Sentry from "@sentry/browser";
import L from "leaflet";
import IconUrl from "leaflet/dist/images/marker-icon.png";
import ShadowUrl from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import "media-chrome";
import MarkerSvg from "./marker.svg";
import parts from "./parts";
import { RasterCoords } from "./rastercoords";
import "./style.css";

const VERSION = 3;

Sentry.init({
	dsn: "https://0627c5b822244a518390d17caf33d80e@sentry.utit.dev/10",
	integrations: [Sentry.browserTracingIntegration()],

	// Set tracesSampleRate to 1.0 to capture 100%
	// of transactions for performance monitoring.
	// We recommend adjusting this value in production
	tracesSampleRate: 1.0,
});

Object.assign(L.Icon.Default.prototype.options, {
	imagePath: "",
	iconUrl: IconUrl,
	shadowUrl: ShadowUrl,
});

const icon = L.icon({
	iconUrl: MarkerSvg,
	iconSize: [30, 30],
	popupAnchor: [0, -20],
});
let mediaMissing = false;

function t(text: string): Node {
	return document.createTextNode(text);
}

function h(
	tag: string,
	attrs: Record<string, string | ((e: Event) => void)>,
	...children: Node[]
): HTMLElement {
	const el = document.createElement(tag);
	for (const [attr, value] of Object.entries(attrs)) {
		if (typeof value === "function") {
			el.addEventListener(attr, value);
		} else {
			el.setAttribute(attr, value);
		}
	}

	for (const child of children) {
		el.appendChild(child);
	}

	return el;
}

let supportedMediaExt: string | null = null;
function getMediaExt(): string {
	if (supportedMediaExt === null) {
		const player = document.createElement("audio");
		// Prefer Opus
		if (player.canPlayType("audio/ogg; codecs=opus") === "probably") {
			supportedMediaExt = ".opus";
			// Use AAC if Opus is not available (mostly Safari)
		} else if (
			player.canPlayType("audio/mp4; codecs=mp4a.40.2") === "probably"
		) {
			supportedMediaExt = ".mp4";
			// Fallback to Opus if neither can be detected
		} else {
			supportedMediaExt = ".opus";
		}
	}

	return supportedMediaExt;
}

async function getMedia(url: string): Promise<Response | undefined> {
	const cache = await caches.open("halligspuk-media");
	return await cache.match(url);
}

async function cacheMedia(display: HTMLDivElement): Promise<void> {
	const label = display.querySelector<HTMLDivElement>(".label")!;
	const mediaCache = await caches.open("halligspuk-media");

	const count = Object.values(parts).length;
	let done = 0;
	for (const item of Object.values(parts)) {
		label.innerText = `Lade Trailer (${done}/${count})`;

		while (true) {
			const url =
				"/halligspuk-media/audio/" + item.trailer + getMediaExt();
			try {
				const response = await fetch(url);
				if (response.status < 200 || response.status >= 300) {
					console.error(
						`[hspk] Failed to load trailer ${url} because of status ${response.status}; trying again...`
					);
					continue;
				}

				mediaCache.put(url, response);
			} catch (e) {
				console.error(
					`[hspk] Failed to load trailer ${url} because of error ${e}; trying again...`
				);

				await new Promise((resolve) => setTimeout(resolve, 300));
			}
			break;
		}
		done++;
	}

	done = 0;
	for (const item of Object.values(parts)) {
		// console.log(`[hspk] Loading trailers [${done}/${count}]`);
		label.innerText = `Lade Hörspiel (${done}/${count})`;

		for (const fullName of item.full) {
			while (true) {
				const url =
					"/halligspuk-media/audio/" + fullName + getMediaExt();
				try {
					const response = await fetch(url);
					if (response.status !== 200) {
						console.error(
							`[hspk] Failed to load full ${url} because of status ${response.status}; trying again...`
						);
						continue;
					}

					mediaCache.put(url, response);
				} catch (e) {
					console.error(
						`[hspk] Failed to load full ${url} because of error ${e}; trying again...`
					);
				}
				break;
			}
		}
		done++;
	}

	label.innerText = "Download wird abgeschlossen...";
}

async function registerWorker(
	button: HTMLAnchorElement,
	progressBox: HTMLDivElement
): Promise<void> {
	const label = progressBox.querySelector<HTMLDivElement>(".label")!;
	progressBox.classList.remove("hidden");
	label.innerText = "Lade...";

	try {
		const result = await navigator.serviceWorker.register("/sw.js");
		const worker = result.installing ?? result.active;
		if (worker === null) {
			console.error("Could not find worker");
			progressBox.classList.add("hidden");
			button.classList.add("cursor-default");
			button.innerHTML =
				"Fehler: Konnte Offline-Modus nicht aktivieren. Unter Umständen wird der Browser nicht unterstützt. Bitte versuche es mit Chrome oder Firefox";
		} else {
			/*
			progressBox.innerHTML = "Fertig! Die Daten wurden heruntergeladen.";
			console.log("worker registered", result);*/
			await cacheMedia(progressBox);
			await navigator.serviceWorker.ready;
			location.reload();
		}
	} catch (e) {
		console.error("Failed to register worker", e);
		progressBox.classList.add("hidden");
		button.classList.add("cursor-default");
		button.innerHTML =
			"Fehler: Die Daten konnten nicht heruntergeladen werden.";
	}
}

function makeInstallPrompt(
	ui: HTMLDivElement,
	progressBox: HTMLDivElement
): HTMLDivElement {
	const cont = h(
		"div",
		{ class: "my-4" },
		h(
			"a",
			{
				class: "block p-4 rounded bg-black text-white",
				href: "#",
				click: (e) => {
					e.preventDefault();
					const button = e.currentTarget as HTMLAnchorElement;
					registerWorker(button, progressBox);
				},
			},
			t("Medien herunterladen")
		),
		h(
			"p",
			{ class: "max-w-prose" },
			t(
				"Um diese Seite auch ohne Internet nutzen zu können, klicken Sie bitte auf diese Schaltfläche. Die Audiodateien werden dann auf Ihr Gerät heruntergeladen, sodass Sie das Hörspiel auch ohne Empfang an den Stationen auf der Hallig hören können."
			)
		)
	);
	ui.appendChild(cont);

	return cont as HTMLDivElement;
}

async function makePlayer(url: string): Promise<string> {
	let href = `/halligspuk-media/audio/${url}${getMediaExt()}`;
	const cached = await getMedia(href);
	if (cached !== undefined) {
		href = URL.createObjectURL(await cached.blob());
	} else {
		mediaMissing = true;
	}

	return `<media-controller audio>
	<audio slot="media" src="${href}"></audio>
	<media-control-bar>
	<media-play-button></media-play-button>
	<media-time-display showduration></media-time-display>
	<media-time-range></media-time-range>
	<media-mute-button></media-mute-button>
	<media-volume-range></media-volume-range>
	</media-control-bar>
</media-controller>`;
}

async function versionUpdate(): Promise<void> {
	try {
		const response = await fetch("/halligspuk-media/version.txt");
		if (response.status === 500) return;

		const version = parseInt(await response.text());
		if (!isNaN(version) && version > VERSION) {
			location.reload();
		}
	} catch (err) {
		Sentry.captureException(err, (scope) => {
			scope.setExtra("detail", "Failed to check for updates");
			return scope;
		});
	}
}
void versionUpdate();

async function init() {
	const minZoom = 0;
	const maxZoom = 4;
	const map = L.map("map", {
		crs: L.CRS.Simple,
		minZoom: minZoom,
		maxZoom: maxZoom,
	});

	const rc = new RasterCoords(map, [3770, 2538]);
	map.setMaxBounds(
		new L.LatLngBounds(
			rc.unproject([-2000, rc.height + 2000]),
			rc.unproject([rc.width + 2000, -2000])
		)
	);

	// the tile layer containing the image generated with gdal2tiles --leaflet ...
	L.tileLayer("/halligspuk-media/halligspuk-karte/{z}/{x}/{y}.png", {
		noWrap: true,
		bounds: rc.getMaxBounds(),
		maxNativeZoom: rc.zoomLevel(),
		attribution: "Gemeinde Hallig Hooge",
	}).addTo(map);

	map.setView(rc.unproject([1500, 1250]), 2);

	// set marker at the image bound edges
	var layerBounds = L.layerGroup([]);
	map.addLayer(layerBounds);

	/*let lastClickMarker = null;
	map.on("click", function (event) {
		// to obtain raster coordinates from the map use `project`
		const coord = rc.project(event.latlng);
		const popup =
			"[" + Math.floor(coord.x) + "," + Math.floor(coord.y) + "]";

		// to set a marker, ... in raster coordinates in the map use `unproject`
		if (lastClickMarker !== null) {
			lastClickMarker.setLatLng(coord).setPopupContent(popup);
		} else {
			lastClickMarker = L.marker(rc.unproject(coord), {
				draggable: true,
				icon,
			}).addTo(layerBounds);
			lastClickMarker.bindPopup(popup).openPopup();
			lastClickMarker.on("dragend", (e) => {
				const coord = rc.project(lastClickMarker.getLatLng());
				const popup =
					"[" + Math.floor(coord.x) + "," + Math.floor(coord.y) + "]";
				lastClickMarker.setPopupContent(popup);
			});
		}
	});*/

	let unlocked: Record<string, boolean> = {};
	try {
		const value = JSON.parse(
			localStorage.getItem("halligspuk-unlocked") ?? "{}"
		);
		if (typeof value === "object") {
			unlocked = value;
		} else {
			console.warn(
				"Invalid value for halligspuk-unlocked found: " +
					(localStorage.getItem("halligspuk-unlocked") ?? "<null>")
			);
		}
	} catch (err) {
		console.error("Failed to load halligspuk-unlocked", err);
	}

	const markers: Record<string, L.Marker> = {};
	for (const [key, part] of Object.entries(parts)) {
		let content = `<h2>${part.title}</h2><p><strong>${part.subtitle}</strong></p><p>${part.text}</p>`;

		if (!unlocked[part.code]) {
			content += `<p>Vorschau:</p>` + (await makePlayer(part.trailer));
			for (const name of part.full) {
				await getMedia(name);
			}
		} else {
			for (const name of part.full) {
				content += await makePlayer(name);
			}
		}

		markers[key] = L.marker(rc.unproject(part.pos), {
			title: part.title,
			riseOnHover: true,
			icon,
		})
			.addTo(layerBounds)
			.bindPopup(content, { maxWidth: 320 });
	}

	async function routeRequest() {
		if (location.hash.startsWith("#/trailer/")) {
			const partName = location.hash.substring(10);
			if (!markers[partName]) {
				// TODO: Error message
				return;
			}

			markers[partName].openPopup();
		}

		if (location.hash.startsWith("#/unlock/")) {
			const code = location.hash.substring(9);

			for (const [key, part] of Object.entries(parts)) {
				if (part.code !== code) continue;

				unlocked[code] = true;
				localStorage.setItem(
					"halligspuk-unlocked",
					JSON.stringify(unlocked)
				);

				let content = `<h2>${part.title}</h2><p><strong>${part.subtitle}</strong></p><p>${part.text}</p>`;
				for (const name of part.full) {
					content += await makePlayer(name);
				}
				markers[key].setPopupContent(content).openPopup();
				break;
			}
		}
	}

	routeRequest();
	window.addEventListener("hashchange", routeRequest);

	const ui = document.querySelector<HTMLDivElement>("#hspk-ui");
	if (!ui) return;

	const progressBox = document.createElement("div");
	progressBox.setAttribute(
		"class",
		"p-4 my-4 rounded-md bg-yellow-200 border border-yellow-100 hidden flex flex-row"
	);
	progressBox.innerHTML = `<div aria-label="Lade..." role="progressbar" size="20"><div class="spinner"><svg width="20" height="20" stroke-width="16.00" viewBox="-3.00 -3.00 106.00 106.00"><path stroke="rgba(95,107,124,.2)" fill="transparent" d="M 50,50 m 0,-45 a 45,45 0 1 1 0,90 a 45,45 0 1 1 0,-90"></path><path d="M 50,50 m 0,-45 a 45,45 0 1 1 0,90 a 45,45 0 1 1 0,-90" pathLength="280" stroke-dasharray="280 280" stroke-dashoffset="210" class="spinner-mover"></path></svg></div></div><div class="label"></div>`;
	ui.appendChild(progressBox);

	let installPrompt: HTMLDivElement | null = null;

	if ("serviceWorker" in navigator) {
		if (mediaMissing) {
			installPrompt = makeInstallPrompt(ui, progressBox);
		} else {
			navigator.serviceWorker.getRegistration().then((worker) => {
				if (!worker) {
					installPrompt = makeInstallPrompt(ui, progressBox);
				} else {
					ui.appendChild(
						h(
							"div",
							{ class: "my-4 p-4" },
							h(
								"p",
								{ class: "max-w-prose" },
								t(
									"Das Hörspiel wurde heruntergeladen und funktioniert jetzt auch ohne Internetverbindung."
								)
							)
						)
					);
				}
			});
		}
	}

	interface BroadcastMessage {
		type: "install-update" | "install-done";
		message?: string;
		progress?: number;
	}

	addEventListener("message", (e) => {
		if (typeof e.data !== "object") return;
		const msg = e.data as BroadcastMessage;
		switch (msg.type) {
			case "install-update":
				if (installPrompt !== null) {
					installPrompt.classList.add("hidden");
				}

				progressBox.classList.remove("hidden");
				progressBox.querySelector<HTMLDivElement>(".label")!.innerText =
					msg.message ?? "Lade...";
				break;
			case "install-done":
				progressBox.classList.add("hidden");
		}
	});
}
init();
